import { Column, Columns, breakpoints, Text } from '@etg/wings';
import { css } from '@eti/styles';
import type {
  BrandedFareFeature,
  BrandedFareInfo,
  CabinBaggageTripSummary,
  CheckedBaggageSummary,
} from '@eti/schema-types';
import { BrandedFareAvailabilityType } from '@eti/schema-types';
import { useTranslation, useProperty } from '@eti/providers';
import IncludedFeatures from './IncludedFeatures';
import NotIncludedFeatures from './NotIncludedFeatures';

const columnsStyles = (brandedFareVersion: number) => css`
  justify-content: space-between;
  ${brandedFareVersion === 1 && 'padding: 16px 0'};
  row-gap: 16px;
`;

const getBrandedFareIncludedFeatures = (brandedFareInformation: BrandedFareInfo) => {
  return brandedFareInformation?.features?.filter((feature) => {
    return feature?.availability === BrandedFareAvailabilityType.Included;
  });
};

const getBrandedFareNotIncludedFeatures = (brandedFareInformation: BrandedFareInfo) => {
  return brandedFareInformation?.features?.filter((feature) => {
    return feature?.availability !== BrandedFareAvailabilityType.Included;
  });
};

interface BrandedFareFeaturesProps {
  cabinBaggageTripSummary: CabinBaggageTripSummary;
  checkedBaggageSummary: CheckedBaggageSummary;
  isPersonalItemIncluded: boolean;
  brandedFareInformation: BrandedFareInfo;
  ticketingCarrierCodes: string[];
}

const BrandedFareFeatures = ({
  cabinBaggageTripSummary,
  checkedBaggageSummary,
  isPersonalItemIncluded,
  brandedFareInformation,
  ticketingCarrierCodes,
}: BrandedFareFeaturesProps) => {
  const { t } = useTranslation();
  const { p } = useProperty();
  const includedFeatures = getBrandedFareIncludedFeatures(brandedFareInformation);
  const notIncludedFeatures = getBrandedFareNotIncludedFeatures(brandedFareInformation);
  return (
    <Columns
      className={columnsStyles(p('PaymentPage.BrandedFare.Version'))}
      collapseBelow={breakpoints._768}
    >
      <IncludedFeatures
        cabinBaggageTripSummary={cabinBaggageTripSummary}
        checkedBaggageSummary={checkedBaggageSummary}
        includedFeatures={includedFeatures as BrandedFareFeature[]}
        isPersonalItemIncluded={isPersonalItemIncluded}
      />
      <NotIncludedFeatures
        cabinBaggageTripSummary={cabinBaggageTripSummary}
        checkedBaggageSummary={checkedBaggageSummary}
        isPersonalItemIncluded={isPersonalItemIncluded}
        notIncludedFeatures={notIncludedFeatures as BrandedFareFeature[]}
        ticketingCarrierCodes={ticketingCarrierCodes}
      />
      <Column width="4/4">
        <Text>{t('PaymentPage.BrandedFare.Note.Abstract')}</Text>
      </Column>
    </Columns>
  );
};

export default BrandedFareFeatures;
